import { datadogRum } from "@datadog/browser-rum";
import { keepPreviousData } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { SelectItem } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportReportTypeChoices,
  ReportTypeEnums,
  useCreateLearnerMutation,
  useCreateReportMutation,
  useGetLearnerFilesByLearnerQuery,
  useGetProviderLearnersQuery,
  useGetReportsQuery,
} from "@api/graphql/types-and-hooks";
import { ReportTypeRedirectMap } from "@components/GoalsBank/constants";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

export const useCreateReportHandler = () => {
  const [isNewReportDialogOpen, setNewReportDialogOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<SelectItem | null>(null);
  const [selectedReportType, setSelectedReportType] = useState<ReportTypeEnums | null>(null);
  const [learnerName, setLearnerName] = useState<string>("");
  const go = useNavigate();

  const reportInitialRoute = `clinical-reports`;

  // Backend search: query uses learnerName. Select debounces onSearch (300ms) so we refetch after user pauses typing.
  const learnerQuery = useGetProviderLearnersQuery(
    { learnerName },
    {
      queryKey: ["get-provider-learners", learnerName],
      placeholderData: keepPreviousData,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  );

  const getClientReport = useGetReportsQuery(
    { filter: { learnerId: selectedClientId, reportType: selectedReportType } },
    {
      queryKey: ["get-client-report", selectedClientId, selectedReportType],
      enabled: !!selectedClientId && !!selectedReportType,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  );

  const previousAssessmentFilesQuery = useGetLearnerFilesByLearnerQuery(
    { learnerId: selectedClientId ?? "missing-learner-id", fileKind: "PREVIOUS_ASSESSMENT", isReportAttached: false },
    {
      queryKey: ["get-client-report", selectedClientId],
      enabled: !!selectedClientId,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const createClientReportMutation = useCreateReportMutation({});
  const createLearnerMutation = useCreateLearnerMutation();
  const { data: createReportData, isPending, error } = createClientReportMutation;

  useEffect(() => {
    setSelectedClientId(null);
    setLearnerName("");
  }, [isNewReportDialogOpen]);

  useEffect(() => {
    if (createReportData?.createReport) {
      const { status, report } = createReportData.createReport;

      if (status && report) {
        notifySuccess(`Report created successfully`);
        const { clientFirstName, clientLastName } = report.reportClientDetails || {};
        setNewReportDialogOpen(false);
        if (ReportTypeRedirectMap.has(report.reportType)) {
          go(`/${reportInitialRoute}/${ReportTypeRedirectMap.get(report.reportType)}/${report.id}`, {
            state: {
              id: report.id,
              clientName: `${clientFirstName || "N/A"} ${clientLastName || ""}`,
              lastUpdated: new Date(report?.lastUpdatedDate) || new Date(),
            },
          });
        } else {
          console.warn("Unhandled report type:", report.reportType);
        }
      } else {
        notifyError(`Error creating Report`);
      }
    }
  }, [createReportData]);

  useEffect(() => {
    if (error) {
      console.error(`Error while creating Report`, error);
      notifyError(`Error creating Report`);
    }
  }, [error]);

  // Resolves true only when a report was created, so the new-report dialog can keep Continue
  // off until it closes (PROA-3064).
  const createReportObject = async (
    fileId?: string,
    learnerId?: string,
    shouldCreateLearner = false,
  ): Promise<boolean> => {
    if (!selectedReportType) return false;

    let effectiveLearnerId: string | null | undefined = learnerId ?? selectedClientId;

    if (shouldCreateLearner && !effectiveLearnerId) {
      try {
        const createLearnerResponse = await createLearnerMutation.mutateAsync({});
        effectiveLearnerId = createLearnerResponse.createLearner?.learner?.id;
        if (!effectiveLearnerId) {
          throw new Error("CreateLearner returned no learner id");
        }
        datadogRum.addAction("create-learner", {});
      } catch (err) {
        console.error("Failed to create sparse learner before report creation", err);
        notifyError("Error creating new client");
        return false;
      }
    }

    const response = await createClientReportMutation.mutateAsync({
      reportType: selectedReportType,
      learnerId: effectiveLearnerId,
      learnerFileId: fileId,
      reportTemplateId: selectedTemplate?.id,
    });

    // log creation to datadog
    const base64DecodedId = atob(response.createReport?.report?.id || "");
    const databaseId = base64DecodedId.split("ReportType:")?.pop();
    datadogRum.addAction("report-creation", {
      reportId: databaseId,
      reportType: selectedReportType,
      reportTemplate: selectedTemplate?.primary,
    });
    return !!response.createReport?.report;
  };

  // Defaults to the client's first report of the selected type, but callers can pass a specific
  // target — e.g. the in-progress reassessment surfaced by the duplicate warning (PROA-1947),
  // which may not be edges[0] when a completed reassessment also exists.
  const goToReport = (targetReport?: { id: string; reportType: ApiReportReportTypeChoices }) => {
    const reportNode = targetReport ?? getClientReport.data?.getReports?.edges[0]?.node;
    if (!reportNode) return;

    setNewReportDialogOpen(false);
    if (ReportTypeRedirectMap.has(reportNode.reportType)) {
      go(`/${reportInitialRoute}/${ReportTypeRedirectMap.get(reportNode.reportType)}/${reportNode.id}`);
    } else {
      console.warn("Unhandled report type:", reportNode.reportType);
    }
  };

  const createButtonDisableStatus = useMemo(
    () => (!!selectedClientId && !!getClientReport.data?.getReports?.edges[0]?.node) || isPending,
    [selectedClientId, getClientReport.data, isPending],
  );

  return {
    isNewReportDialogOpen,
    setNewReportDialogOpen,
    selectedClientId,
    setSelectedClientId,
    learnerQuery,
    getClientReport,
    createButtonDisableStatus,
    createReportObject,
    goToReport,
    isReportCreating: isPending || createLearnerMutation.isPending,
    previousAssessmentFilesQuery,
    selectedReportType,
    setSelectedReportType,
    selectedTemplate,
    setSelectedTemplate,
    learnerName,
    setLearnerName,
  };
};
